<script lang="ts">
import type { ImagePreviewTool } from '@satellite/components/elements/preview/ImagePreviewDialog.vue';
import type { PdfPreviewTool } from '@satellite/components/elements/preview/PdfPreviewDialog.vue';

// Module scope: prop defaults are hoisted out of `setup`, so they cannot read its bindings.
const DEFAULT_IMAGE_TOOLS: ImagePreviewTool[] = ['zoom', 'pan', 'download'];
const DEFAULT_PDF_TOOLS: PdfPreviewTool[] = ['zoom', 'download'];
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import ImagePreviewDialog from '@satellite/components/elements/preview/ImagePreviewDialog.vue';
import PdfPreviewDialog from '@satellite/components/elements/preview/PdfPreviewDialog.vue';
import documentService from '@satellite/services/document-service';
import { isImageAttachment, isPdfAttachment } from '@satellite/helpers/attachmentUtils';

import type { Maybe, YardTaskAttachment } from '@nova/core';

defineOptions({ name: 'AttachmentPreview' });

const {
  imageTools = DEFAULT_IMAGE_TOOLS,
  pdfTools = DEFAULT_PDF_TOOLS,
  testId = 'attachment'
} = defineProps<{
  /** Which controls the image preview offers. One image at a time, so no navigation. */
  imageTools?: ImagePreviewTool[];
  pdfTools?: PdfPreviewTool[];
  /** Prefix for the dialogs' `data-testid`s, e.g. `<testId>-preview-close`. */
  testId?: string;
}>();

/** The attachment on screen. Setting it opens the matching preview; clearing it closes. */
const attachment = defineModel<Maybe<YardTaskAttachment>>({ default: null });

/**
 * Held past the close on purpose: dropping the attachment as the dialog leaves would empty it
 * mid-transition, collapsing it to its bars on the way out.
 */
const previewedAttachment = ref<Maybe<YardTaskAttachment>>(attachment.value);

watch(attachment, value => {
  if (value) {
    previewedAttachment.value = value;
  }
});

const isImagePreviewOpen = computed({
  get: () => Boolean(attachment.value && isImageAttachment(attachment.value)),
  set: value => {
    if (!value) {
      attachment.value = null;
    }
  }
});

const isPdfPreviewOpen = computed({
  get: () => Boolean(attachment.value && isPdfAttachment(attachment.value)),
  set: value => {
    if (!value) {
      attachment.value = null;
    }
  }
});

/** A single-item list: the preview renders plainly, with no navigation between attachments. */
const images = computed(() =>
  previewedAttachment.value && isImageAttachment(previewedAttachment.value)
    ? [previewedAttachment.value.url]
    : []
);

/**
 * Read through the API rather than from the presigned URL: an `<img>` needs no CORS, but
 * fetching the bytes does, and the bucket has none. Without it the browser can only offer to
 * open the file in a tab, which a standalone PWA cannot come back from.
 */
function loadDocument(): Promise<string> {
  return documentService.getDownloadBlobUrl(previewedAttachment.value?.id ?? '');
}
</script>

<template>
  <image-preview-dialog
    v-model="isImagePreviewOpen"
    :images="images"
    :file-name="previewedAttachment?.name ?? ''"
    :tools="imageTools"
    :download-source="loadDocument"
    :test-id="`${testId}-preview`" />

  <pdf-preview-dialog
    v-model="isPdfPreviewOpen"
    :src="loadDocument"
    :file-name="previewedAttachment?.name ?? ''"
    :tools="pdfTools"
    :test-id="`${testId}-pdf-preview`">
    <template #error>
      <!-- @slot Shown when the document cannot be read. -->
      <slot name="pdf-error" />
    </template>
  </pdf-preview-dialog>
</template>
